import {
  collection,
  doc,
  endAt,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  startAfter,
  startAt,
  Timestamp,
  Unsubscribe,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { User as FirebaseUser } from 'firebase/auth';
import { db, functions } from '../platform/firebase/clients';
import { User } from '../types';
import { logGlobalError } from '../utils/globalErrorLogger';
import { wrapSubscriptionError } from './subscriptionError';

export interface UserRoles {
  isAdmin: boolean;
  isReviewer: boolean;
  reviewCampaignIds: string[];
  trustedCampaignIds?: string[];
  expertId: string | null;
}

const USERS = 'users';

/** Normalize the value stored in the user search indexes and the query input. */
export function normalizeUserSearchText(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

type UserSearchField = 'displayNameLower' | 'emailLower';

/** Email-shaped input searches the email index; all other input searches names. */
export function userSearchField(searchQuery: string): UserSearchField {
  return normalizeUserSearchText(searchQuery).includes('@')
    ? 'emailLower'
    : 'displayNameLower';
}

function userSearchFields(user: FirebaseUser): Record<'emailLower' | 'displayNameLower', string> {
  return {
    emailLower: normalizeUserSearchText(user.email),
    displayNameLower: normalizeUserSearchText(user.displayName),
  };
}

function toDate(value: unknown): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return new Date(0);
}

function mapUser(id: string, data: Record<string, any>): User {
  return {
    uid: data.uid ?? id,
    email: data.email ?? null,
    displayName: data.displayName ?? null,
    createdAt: toDate(data.createdAt),
    lastLoginAt: toDate(data.lastLoginAt),
    isAdmin: data.isAdmin === true,
    isReviewer: data.isReviewer === true,
    reviewCampaignIds: Array.isArray(data.reviewCampaignIds) ? data.reviewCampaignIds : [],
    trustedCampaignIds: Array.isArray(data.trustedCampaignIds) ? data.trustedCampaignIds : [],
    expertId: data.expertId ?? undefined,
    unsubscribeToken: data.unsubscribeToken ?? null,
    notifyOnApproved: data.notifyOnApproved !== false,
    notifyOnChangesRequested: data.notifyOnChangesRequested !== false,
    aiDetectionTopWarningQuartile: data.aiDetectionTopWarningQuartile === true,
  };
}

/**
 * Live list of all users, ordered by display name. Used by the Browse Tasks
 * admin tool to populate the owner picker — relies on `/users` being readable
 * by any signed-in user. The admin Users tool uses `fetchUsersPage` instead,
 * which paginates server-side.
 */
export function subscribeToUsers(
  onChange: (users: User[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, USERS), orderBy('displayName', 'asc'));
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => mapUser(d.id, d.data()))),
    wrapSubscriptionError('users.subscribeToUsers', onError),
  );
}

/**
 * Filter applied to a paginated users query. Mutually exclusive — combining
 * facets would multiply the composite indexes we need to maintain, and the
 * admin tool's UX exposes them as a single tab group anyway. `campaign`
 * matches users with the given campaign id in `reviewCampaignIds` (i.e. the
 * campaign's assigned reviewers); experts working on the campaign are not
 * included here — they're surfaced from the campaign-side tools instead.
 */
export type UsersListFilter =
  | { kind: 'all' }
  | { kind: 'admins' }
  | { kind: 'reviewers' }
  | { kind: 'campaign'; campaignId: string };

export interface FetchUsersPageOptions {
  filter: UsersListFilter;
  /**
   * Optional case-insensitive prefix. Name-shaped input matches
   * `displayNameLower`; input containing `@` matches `emailLower`.
   * Empty / whitespace = no search filter.
   */
  searchQuery?: string;
  pageSize: number;
  /** Last document of the previous page; omit for the first page. */
  cursor?: QueryDocumentSnapshot | null;
}

export interface UsersPage {
  users: User[];
  /** Last doc of this page — pass as `cursor` to fetch the next page. */
  lastDoc: QueryDocumentSnapshot | null;
  hasMore: boolean;
}

function usersQueryConstraints(
  filter: UsersListFilter,
  searchQuery: string | undefined,
): QueryConstraint[] {
  const constraints: QueryConstraint[] = [];
  switch (filter.kind) {
    case 'admins':
      constraints.push(where('isAdmin', '==', true));
      break;
    case 'reviewers':
      constraints.push(where('isReviewer', '==', true));
      break;
    case 'campaign':
      constraints.push(where('reviewCampaignIds', 'array-contains', filter.campaignId));
      break;
    case 'all':
      break;
  }
  const prefix = normalizeUserSearchText(searchQuery);
  const sortField = prefix ? userSearchField(prefix) : 'displayName';
  constraints.push(orderBy(sortField, 'asc'));
  if (prefix) {
    // Prefix range on a normalized search field. The `\uf8ff` tail is the standard
    // Firestore trick for "any character after `prefix`".
    constraints.push(startAt(prefix), endAt(prefix + '\uf8ff'));
  }
  return constraints;
}

/**
 * One-shot paginated read of users matching `filter` (+ optional name/email prefix).
 * Uses cursor-based pagination: callers track the last doc per page so they
 * can step forward; stepping backward is a re-fetch with the previous page's
 * cursor. Fetches `pageSize + 1` docs to detect whether more pages exist
 * without an extra round-trip.
 */
export async function fetchUsersPage(opts: FetchUsersPageOptions): Promise<UsersPage> {
  const constraints = usersQueryConstraints(opts.filter, opts.searchQuery);
  if (opts.cursor) constraints.push(startAfter(opts.cursor));
  constraints.push(limit(opts.pageSize + 1));
  const snap = await getDocs(query(collection(db, USERS), ...constraints));
  const hasMore = snap.docs.length > opts.pageSize;
  const pageDocs = hasMore ? snap.docs.slice(0, opts.pageSize) : snap.docs;
  const users = pageDocs.map((d) => mapUser(d.id, d.data()));
  const lastDoc = pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null;
  return { users, lastDoc, hasMore };
}

/**
 * Server-side count for the same filter shape. Drives the "X total" /
 * "X of Y" labels and the page-count math without us having to fetch every
 * matching doc to count it.
 */
export async function countUsers(
  filter: UsersListFilter,
  searchQuery?: string,
): Promise<number> {
  // Range filters (startAt/endAt) are accepted by getCountFromServer the same
  // way they are by getDocs, so the constraint set carries over unchanged.
  const constraints = usersQueryConstraints(filter, searchQuery);
  const snap = await getCountFromServer(query(collection(db, USERS), ...constraints));
  return snap.data().count;
}

export interface UserRolePatch {
  isAdmin?: boolean;
  isReviewer?: boolean;
  reviewCampaignIds?: string[];
  trustedCampaignIds?: string[];
}

export interface AlignedReviewerSyncSkip {
  campaignId: string;
  reason: string;
}

export interface AlignedReviewerSyncSummary {
  updated: string[];
  unchanged: string[];
  skipped: AlignedReviewerSyncSkip[];
}

export interface UpdateUserRolesResult {
  uid: string;
  alignedSync: AlignedReviewerSyncSummary;
}

/**
 * Admin-only update of role fields on a user doc, routed through the
 * `updateUserRoles` callable so that reviewer-campaign changes are mirrored
 * onto Aligned's `campaign_experts.isReviewer` before Workbench is written.
 * The result says, per campaign, whether Aligned was updated or why not.
 */
export async function updateUserRoles(
  uid: string,
  patch: UserRolePatch,
): Promise<UpdateUserRolesResult> {
  const fn = httpsCallable<{ uid: string; patch: UserRolePatch }, UpdateUserRolesResult>(
    functions,
    'updateUserRoles',
  );
  const res = await fn({ uid, patch });
  return res.data;
}

export interface SyncUserUnsubscribeTokenResult {
  expertId: string;
  hasUnsubscribeToken: boolean;
}

export async function syncUserUnsubscribeToken(
  uid: string,
): Promise<SyncUserUnsubscribeTokenResult> {
  const fn = httpsCallable<{ uid: string }, SyncUserUnsubscribeTokenResult>(
    functions,
    'syncUserUnsubscribeToken',
  );
  const res = await fn({ uid });
  return res.data;
}

export interface NotificationSettings {
  notifyOnApproved: boolean;
  notifyOnChangesRequested: boolean;
}

/**
 * Reads the signed-in user's profile fields shown on the settings page.
 * Missing notification flags default to enabled.
 */
export async function getUserProfile(uid: string): Promise<User | null> {
  const snap = await getDoc(doc(db, USERS, uid));
  if (!snap.exists()) return null;
  return mapUser(snap.id, snap.data());
}

/**
 * User-facing update of notification preferences. Rules allow a user to
 * self-update only the fields listed in the `users` match block.
 */
export async function updateNotificationSettings(
  uid: string,
  patch: Partial<NotificationSettings>,
): Promise<void> {
  const entries = Object.entries(patch);
  if (entries.length === 0) throw new Error('Notification settings patch cannot be empty.');
  for (const [field, value] of entries) {
    if (!['notifyOnApproved', 'notifyOnChangesRequested'].includes(field)
      || typeof value !== 'boolean') {
      throw new Error('Notification settings patch is invalid.');
    }
  }
  await updateDoc(doc(db, USERS, uid), { ...patch });
}

/**
 * Single round-trip user bootstrap used by the auth handler.
 *
 * Reads `users/{uid}` once and returns the caller's roles. The `lastLoginAt`
 * (and for new users, the full profile) write is dispatched but not awaited —
 * it's a best-effort bookkeeping update that must not delay the signed-in UX.
 * New users are linked to Aligned only after the profile write completes, so
 * the callable cannot create a partial user document first.
 */
export async function bootstrapUser(user: FirebaseUser): Promise<UserRoles> {
  const ref = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    void setDoc(ref, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      ...userSearchFields(user),
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    }).then(() => linkExpertIdIfNeeded(true)).catch((err) => {
      logGlobalError(err, {
        context: 'users.bootstrapUser.createUserDoc',
        metadata: { uid: user.uid },
      });
    });
    return { isAdmin: false, isReviewer: false, reviewCampaignIds: [], expertId: null };
  }

  const data = snap.data();
  const isAdmin = data.isAdmin === true;
  const isReviewer = isAdmin || data.isReviewer === true;
  const reviewCampaignIds = Array.isArray(data.reviewCampaignIds) ? data.reviewCampaignIds : [];
  const expertId = typeof data.expertId === 'string' ? data.expertId : null;

  void setDoc(
    ref,
    {
      email: user.email,
      displayName: user.displayName,
      ...userSearchFields(user),
      lastLoginAt: serverTimestamp(),
    },
    { merge: true },
  ).catch((err) =>
    logGlobalError(err, {
      context: 'users.bootstrapUser.refreshUserDoc',
      metadata: { uid: user.uid },
    }),
  );

  // Only attempt the Aligned lookup if we haven't yet written *any* value for
  // expertId (including `null` for "not found"). This avoids re-querying every
  // sign-in for users who aren't in Aligned.
  if (data.expertId === undefined) {
    void linkExpertIdIfNeeded(false);
  }

  return { isAdmin, isReviewer, reviewCampaignIds, expertId, trustedCampaignIds: Array.isArray(data.trustedCampaignIds) ? data.trustedCampaignIds : [] };
}

async function linkExpertIdIfNeeded(isNewUser: boolean): Promise<void> {
  try {
    const fn = httpsCallable<unknown, { expertId: string | null; skipped?: string }>(
      functions,
      'linkExpertId',
    );
    await fn();
  } catch (err) {
    logGlobalError(err, {
      context: 'users.linkExpertIdIfNeeded',
      metadata: { isNewUser },
    });
  }
}
